import React, { useState } from 'react';
import { LuX, LuMail, LuUser, LuShield, LuLoader, LuPhone, LuMessageSquare, LuDollarSign, LuLock } from 'react-icons/lu';
import { teamService } from '../../services/teamService';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';

const AddMemberModal = ({ isOpen, onClose, onInviteSuccess }) => {
    if (!isOpen) return null;

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        full_name: '', // Added full name for better UX
        role: 'Sales',
        team_id: '',   // Optional, but good to have if we want to assign directly
        phone: '',
        line_id: '',
        whatsapp_id: '',
        commission_rate: 0
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const result = await teamService.inviteMember(
            formData.email,
            formData.role,
            formData.team_id || null,
            {
                password: formData.password,
                full_name: formData.full_name,
                phone: formData.phone,
                line_id: formData.line_id,
                whatsapp_id: formData.whatsapp_id,
                commission_rate: parseFloat(formData.commission_rate)
            }
        );

        if (result.success) {
            toast.success('Member invited successfully!');
            onInviteSuccess();
            onClose();
        } else {
            toast.error(result.error?.message || 'Failed to invite member');
        }
        setLoading(false);
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">

                    {/* Header */}
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                            Invite Team Member
                        </h3>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                            <SafeIcon icon={LuX} className="h-6 w-6" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                            <div className="space-y-4">

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Email Address <span className="text-red-500">*</span></label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <SafeIcon icon={LuMail} className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="email"
                                            required
                                            className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                                            placeholder="colleague@example.com"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* Password (New) */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Password <span className="text-red-500">*</span></label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <SafeIcon icon={LuLock} className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="password"
                                            required
                                            minLength={6}
                                            className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                                            placeholder="Set initial password"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        />
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">Must be at least 6 characters.</p>
                                </div>

                                {/* Full Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Full Name</label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <SafeIcon icon={LuUser} className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                                            placeholder="John Doe"
                                            value={formData.full_name}
                                            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* Role */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Role <span className="text-red-500">*</span></label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <SafeIcon icon={LuShield} className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <select
                                            required
                                            className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                                            value={formData.role}
                                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                        >
                                            <option value="Sales">Sales</option>
                                            <option value="Manager">Manager</option>
                                            <option value="Admin">Admin</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Commission Rate */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Commission Rate (%)</label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <SafeIcon icon={LuDollarSign} className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                                            placeholder="0"
                                            value={formData.commission_rate}
                                            onChange={(e) => setFormData({ ...formData, commission_rate: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* Extra Details Toggle/Section could go here if needed, but keeping simple for now */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs text-gray-500">Phone</label>
                                        <input
                                            type="text"
                                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500">Line ID</label>
                                        <input
                                            type="text"
                                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            value={formData.line_id}
                                            onChange={(e) => setFormData({ ...formData, line_id: e.target.value })}
                                        />
                                    </div>
                                </div>

                            </div>
                        </div>

                        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-blue-400"
                            >
                                {loading && <SafeIcon icon={LuLoader} className="animate-spin -ml-1 mr-2 h-4 w-4" />}
                                {loading ? 'Creating...' : 'Create Member'}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={loading}
                                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddMemberModal;
